import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildProfileCompletionSummary } from '@/lib/profileCompletion';
import {
  getJwtSessionId,
  PROFILE_ONBOARDING_POLICY_VERSION,
} from '@/lib/profileOnboarding';
import { supabase } from '@/lib/supabase';
import type {
  Database,
  MemberPayrollDetails,
  Profile,
} from '@/types/database.types';
import type { Role } from '@/types/roles';

type OnboardingProgress =
  Database['public']['Tables']['profile_onboarding_progress']['Row'];

interface UseProfileOnboardingOptions {
  userId: string | undefined;
  accessToken: string | null | undefined;
  organizationId: string | null;
  role: Role | null;
  enabled: boolean;
}

const ONBOARDING_PROFILE_SELECT = `
  full_name,
  phone,
  dob,
  address_unit,
  address_street,
  address_suburb,
  address_state,
  address_postcode,
  residency_status,
  visa_subclass,
  visa_expiry_date,
  emergency_contact_name,
  emergency_contact_relationship,
  emergency_contact_phone,
  availability_confirmed
`;

export function useProfileOnboarding({
  userId,
  accessToken,
  organizationId,
  role,
  enabled,
}: UseProfileOnboardingOptions) {
  const queryClient = useQueryClient();
  const scopeKey = organizationId ?? 'platform';
  const currentSessionId = getJwtSessionId(accessToken);
  const queryKey = [
    'profile-onboarding',
    PROFILE_ONBOARDING_POLICY_VERSION,
    userId,
    scopeKey,
    role,
  ] as const;

  const query = useQuery({
    queryKey,
    enabled: enabled && !!userId && !!role,
    queryFn: async () => {
      const profileRequest = supabase
        .from('profiles')
        .select(ONBOARDING_PROFILE_SELECT)
        .eq('user_id', userId!)
        .maybeSingle();

      const progressRequest = supabase
        .from('profile_onboarding_progress')
        .select('*')
        .eq('user_id', userId!)
        .eq('scope_key', scopeKey)
        .eq('policy_version', PROFILE_ONBOARDING_POLICY_VERSION)
        .maybeSingle();

      const payrollRequest = organizationId
        ? supabase
          .from('member_payroll_details')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('employee_user_id', userId!)
          .maybeSingle()
        : Promise.resolve({ data: null, error: null });

      const documentsRequest = organizationId
        ? supabase
          .from('documents')
          .select('document_type, deleted_at')
          .eq('organization_id', organizationId)
          .eq('employee_user_id', userId!)
          .is('deleted_at', null)
        : Promise.resolve({ data: [], error: null });

      const [profileResult, payrollResult, documentsResult, progressResult] =
        await Promise.all([
          profileRequest,
          payrollRequest,
          documentsRequest,
          progressRequest,
        ]);

      const error =
        profileResult.error ??
        payrollResult.error ??
        documentsResult.error ??
        progressResult.error;
      if (error) throw error;

      const profile = profileResult.data as Partial<Profile> | null;
      const progress = progressResult.data as OnboardingProgress | null;
      const summary = buildProfileCompletionSummary({
        role,
        profile,
        payroll: payrollResult.data as Partial<MemberPayrollDetails> | null,
        documents: documentsResult.data ?? [],
        availabilityConfirmed: Boolean(profile?.availability_confirmed),
      });

      return {
        summary,
        progress,
        deferredInCurrentSession: Boolean(
          currentSessionId && progress?.last_deferred_session_id === currentSessionId,
        ),
      };
    },
    staleTime: 30_000,
  });

  const deferMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('defer_my_profile_onboarding', {
        p_organization_id: organizationId,
        p_policy_version: PROFILE_ONBOARDING_POLICY_VERSION,
      });
      if (error) throw error;
      return data as OnboardingProgress;
    },
    onSuccess: (progress) => {
      queryClient.setQueryData(queryKey, (current: typeof query.data) =>
        current
          ? {
            ...current,
            progress,
            deferredInCurrentSession: true,
          }
          : current,
      );
    },
  });

  return {
    ...query,
    defer: deferMutation.mutateAsync,
    isDeferring: deferMutation.isPending,
    deferError: deferMutation.error,
  };
}
